import React, { useState } from "react";
import "./AddRecipeForm.scss";

const AddRecipeForm = ({ allFilters, onClose, onRecipeAdded }) => {
  // Which recipe creation experience is active: "external" or "manual"
  const [recipeType, setRecipeType] = useState("external");
  const isManual = recipeType === "manual";

  // State for the recipe name input field
  const [name, setName] = useState("");

  // State for the external link input field
  const [link, setLink] = useState("");

  // State for the selected tags, one per filter category
  const [tags, setTags] = useState({});

  // State for the uploaded image file (manual recipes)
  const [imageFile, setImageFile] = useState(null);

  // State for an externally-hosted image URL (external recipes)
  const [imageUrl, setImageUrl] = useState("");

  // State for submission status
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // State for managing dynamic ingredients inputs
  const [ingredients, setIngredients] = useState([{ value: "" }]); // Array to store ingredient inputs

  // State for managing dynamic instructions inputs (title and description)
  const [instructions, setInstructions] = useState([
    { title: "", description: "" },
  ]);

  const handleTagChange = (category, value) => {
    setTags((prev) => ({ ...prev, [category]: value }));
  };

  const handleImageChange = (e) => {
    setImageFile(e.target.files[0] || null);
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Handle change for the name input
  const handleNameChange = (e) => {
    setName(e.target.value);
  };

  // Handle change for the ingredient input
  const handleIngredientChange = (index, e) => {
    const updatedIngredients = [...ingredients];
    updatedIngredients[index].value = e.target.value;
    setIngredients(updatedIngredients);
  };

  // Handle adding a new ingredient input
  const handleAddIngredient = () => {
    setIngredients([...ingredients, { value: "" }]); // Add a new ingredient input field
  };

  // Handle removing an ingredient input
  const handleRemoveIngredient = (index) => {
    const updatedIngredients = ingredients.filter((_, i) => i !== index);
    setIngredients(updatedIngredients);
  };

  // Handle adding a new instruction
  const handleAddInstruction = () => {
    // Add a new instruction object with empty title and description
    setInstructions([...instructions, { title: "", description: "" }]);
  };

  // Handle change for instruction title input
  const handleInstructionTitleChange = (index, e) => {
    const updatedInstructions = [...instructions];
    updatedInstructions[index].title = e.target.value;
    setInstructions(updatedInstructions);
  };

  // Handle change for instruction description input
  const handleInstructionDescriptionChange = (index, e) => {
    const updatedInstructions = [...instructions];
    updatedInstructions[index].description = e.target.value;
    setInstructions(updatedInstructions);
  };

  // Handle removing an instruction
  const handleRemoveInstruction = (index) => {
    const updatedInstructions = instructions.filter((_, i) => i !== index);
    setInstructions(updatedInstructions);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create the object to be sent to the API
    const recipeObject = {
      name,
      recipeType,
      tags: Object.values(tags).filter(Boolean),
    };

    if (isManual) {
      // Generate the array of ingredients from the input fields
      recipeObject.ingredients = ingredients
        .map((ingredient) => ingredient.value.trim())
        .filter((ingredient) => ingredient !== ""); // Filter out empty strings

      // Generate the array of instructions
      recipeObject.instructions = instructions
        .map(({ title, description }) => formatInstruction(title, description))
        .filter((instruction) => instruction !== ""); // Filter out empty instructions
    } else {
      recipeObject.link = link.trim() || undefined;
      recipeObject.image = imageUrl.trim() || undefined;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (isManual && imageFile) {
        const dataUrl = await readFileAsDataUrl(imageFile);
        const [, imageData] = dataUrl.split(",");
        recipeObject.imageData = imageData;
        recipeObject.imageType = imageFile.type;
      }

      const response = await fetch("/api/add-recipe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(recipeObject),
      });

      if (response.status === 401) {
        throw new Error("Your session expired, please reopen the form to sign in again");
      }

      if (!response.ok) {
        throw new Error("Failed to save recipe");
      }

      const { recipe } = await response.json();
      onRecipeAdded(recipe);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Function to format the instruction.title and instruction.description into a single string
  const formatInstruction = (title, description) => {
    if (title.trim() === "") {
      // If title is empty, return only the description
      return description;
    }
    // If title exists, return the formatted string with the title as bold
    return `<b>${title}</b> ${description}`;
  };

  return (
    <div className="add-recipe-form">
      <div className="backdrop" />
      <div className="modal">
        <div className="wrapper">
          <h2>Add Recipe</h2>
          <button className="close-button primary" onClick={onClose}>
            Cancel
          </button>
          <form onSubmit={handleSubmit}>
            <div>
              <label htmlFor="name">
                <h3>Title:</h3>
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={handleNameChange}
                placeholder="Enter recipe name"
                required
              />
            </div>

            <div>
              <h3>Tags:</h3>
              {Object.entries(allFilters || {}).map(([category, options]) => (
                <div className="tag-category" key={category}>
                  <label htmlFor={`tag-${category}`}>{category}:</label>
                  <select
                    id={`tag-${category}`}
                    value={tags[category] || ""}
                    onChange={(e) => handleTagChange(category, e.target.value)}
                  >
                    <option value="">--</option>
                    {options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div className="recipe-type-tabs">
              <button
                className={`tab ${!isManual ? "active" : ""}`}
                type="button"
                onClick={() => setRecipeType("external")}
              >
                From the Web
              </button>
              <button
                className={`tab ${isManual ? "active" : ""}`}
                type="button"
                onClick={() => setRecipeType("manual")}
              >
                Manual Entry
              </button>
            </div>

            {isManual ? (
              <>
                <div>
                  <label htmlFor="image">
                    <h3>Image:</h3>
                  </label>
                  <input
                    className="auto-size"
                    type="file"
                    id="image"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </div>

                <div>
                  <h3>Ingredients:</h3>
                  {ingredients.map((ingredient, index) => (
                    <div className="ingredient-item" key={index}>
                      <input
                        type="text"
                        value={ingredient.value}
                        onChange={(e) => handleIngredientChange(index, e)}
                        placeholder="Enter an ingredient"
                      />
                      <button
                        className="primary remove-item"
                        type="button"
                        onClick={() => handleRemoveIngredient(index)}
                      >
                        X
                      </button>
                    </div>
                  ))}
                  <button
                    className="add-item"
                    type="button"
                    onClick={handleAddIngredient}
                  >
                    Add Ingredient
                  </button>
                </div>

                <div>
                  <h3>Instructions:</h3>
                  {instructions.map((instruction, index) => (
                    <div className="instruction-item" key={index}>
                      <div>
                        <div className="instruction-input-wrapper">
                          <label htmlFor={`instructionTitle-${index}`}>
                            Header:
                          </label>
                          <input
                            type="text"
                            id={`instructionTitle-${index}`}
                            value={instruction.title}
                            onChange={(e) =>
                              handleInstructionTitleChange(index, e)
                            }
                            placeholder="Enter instruction header (optional)"
                          />
                        </div>

                        <div className="instruction-input-wrapper">
                          <label htmlFor={`instructionDescription-${index}`}>
                            Details:
                          </label>
                          <textarea
                            type="text"
                            id={`instructionDescription-${index}`}
                            value={instruction.description}
                            onChange={(e) =>
                              handleInstructionDescriptionChange(index, e)
                            }
                            placeholder="Enter instructions"
                          />
                        </div>
                      </div>

                      <button
                        className="primary remove-item"
                        type="button"
                        onClick={() => handleRemoveInstruction(index)}
                      >
                        X
                      </button>
                    </div>
                  ))}
                  <button
                    className="add-item"
                    type="button"
                    onClick={handleAddInstruction}
                  >
                    Add a Step
                  </button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label htmlFor="link">
                    <h3>External Link (HelloFresh, etc):</h3>
                  </label>
                  <input
                    type="url"
                    id="link"
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label htmlFor="imageUrl">
                    <h3>Image URL:</h3>
                  </label>
                  <input
                    type="url"
                    id="imageUrl"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </>
            )}

            {error ? <div className="error">{error}</div> : null}

            <button
              className="primary submit"
              type="submit"
              disabled={
                !name ||
                submitting ||
                (isManual
                  ? !ingredients.length || !instructions.length
                  : !link.trim())
              }
            >
              {submitting ? "Saving..." : "Submit Recipe"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddRecipeForm;
